import { useMutation } from "@apollo/client/react";
import { DSBadge } from "@components/design-system/molecules";
import { DSTabs } from "@components/design-system/organisms";
import { DSModal } from "@components/design-system/organisms";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { faLayerGroup } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { defaultErrorHandler } from "@src/utils/operationTracking";
import { useState } from "react";
import { useNavigate } from "react-router";

import { UNGROUP_NOTE } from "./api/mutations";

const STATUS_BADGE_MAP = {
  REVIEW: { label: "Review", variant: "green" },
  EXPORT: { label: "Done", variant: "green" },
  EXPORTED: { label: "Exported", variant: "blue" },
  PROCESSING: { label: "Processing", variant: "yellow" },
  UNPROCESSED: { label: "Draft", variant: "brand" },
};

function TabStatusBadge({ status }) {
  const config = STATUS_BADGE_MAP[status];
  if (!config) return null;

  return (
    <DSBadge
      variant={config.variant}
      size="sm"
      dataTestId={`tab-status-${status.toLowerCase()}`}
    >
      {config.label}
    </DSBadge>
  );
}

export function GroupedNoteHeader({
  noteGroup,
  activeNoteUuid,
  noteUuidsWithNewAudio = new Set(),
}) {
  const navigate = useNavigate();

  const [ungroupNote, { loading: ungrouping }] =
    useMutation(UNGROUP_NOTE);
  const [showConfirm, setShowConfirm] = useState(false);

  const { name, notes = [] } = noteGroup;

  const sortedNotes = notes
    .filter((entry) => entry?.note?.uuid)
    .sort((a, b) => a.position - b.position);

  const handleTabChange = (uuid) => {
    if (uuid !== activeNoteUuid) {
      navigate(`/edit_note/${uuid}`);
    }
  };

  const handleUngroupClick = (e) => {
    e.stopPropagation();
    setShowConfirm(true);
  };

  const handleConfirmUngroup = () => {
    ungroupNote({
      variables: { noteUuid: activeNoteUuid },
      refetchQueries: ["getNote"],
    })
      .then(() => {
        setShowConfirm(false);
      })
      .catch(defaultErrorHandler);
  };

  return (
    <div className="mb-4">
      <div className="flex items-center gap-sm mb-4">
        <span className="text-brand text-lg">
          <FontAwesomeIcon icon={faLayerGroup} />
        </span>
        <span className="text-primary font-semibold">
          {name || "Grouped Note"}
        </span>
        <DSBadge
          variant="brand"
          size="sm"
          dataTestId="grouped-note-count"
        >
          {sortedNotes.length} notes
        </DSBadge>
      </div>

      <div className="[&_[role=tablist]]:flex-nowrap">
        <DSTabs
          value={activeNoteUuid}
          onChange={handleTabChange}
          dataTestId="groupedNoteTabs"
        >
          <DSTabs.List>
            {sortedNotes.map(({ note }) => (
              <DSTabs.Tab
                key={note.uuid}
                value={note.uuid}
                activeColor="bg-brand"
              >
                <span className="flex items-center gap-sm min-w-0 overflow-hidden">
                  <span className="truncate shrink">
                    {note.jobTypeName || "Note"}
                  </span>
                  <span className="shrink-0">
                    <TabStatusBadge status={note.status} />
                  </span>
                  {noteUuidsWithNewAudio.has(note.uuid) && (
                    <span className="shrink-0">
                      <DSBadge
                        variant="cyan"
                        size="sm"
                        dataTestId="new-audio-indicator"
                      >
                        NEW AUDIO
                      </DSBadge>
                    </span>
                  )}
                  {note.uuid === activeNoteUuid && (
                    <button
                      onClick={handleUngroupClick}
                      disabled={ungrouping}
                      className="flex items-center justify-center w-5 h-5 rounded-full bg-danger hover:bg-placeholder transition-colors -ml-1 absolute right-2"
                      aria-label="Remove note from group"
                      data-testid="ungroup-note-button"
                    >
                      <FontAwesomeIcon
                        icon={faXmark}
                        className="text-xs text-secondary"
                      />
                    </button>
                  )}
                </span>
              </DSTabs.Tab>
            ))}
          </DSTabs.List>
          <DSTabs.Panel value={activeNoteUuid}>{null}</DSTabs.Panel>
        </DSTabs>
      </div>

      <DSModal
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        title="Remove from Group"
        actions={[
          {
            label: "Cancel",
            variant: "cancel",
            onClick: () => setShowConfirm(false),
            disabled: ungrouping,
          },
          {
            label: ungrouping ? "Removing..." : "Remove",
            variant: "primary",
            onClick: handleConfirmUngroup,
            disabled: ungrouping,
            dataTestId: "confirm-ungroup-button",
          },
        ]}
        dataTestId="ungroup-note-modal"
      >
        <p>
          This will remove the current note from{" "}
          <strong>{name || "this group"}</strong> and make it a
          standalone note. The other notes in the group will not be
          affected. This note will not be modified or deleted.
        </p>
        {sortedNotes.length <= 2 && (
          <p className="mt-sm text-secondary text-sm">
            Since there will only be one note remaining, the group
            will be dissolved.
          </p>
        )}
      </DSModal>
    </div>
  );
}
